/**
 * @praesto/core/tenant — tenant entity + settings, reactive.
 *
 * Mirrors firestore.rules: settings live at /tenants/{tid}/settings/general
 * (the doc `isModuleActive()` reads) and the tenant entity at /tenants/{tid}.
 * Both are observed via onSnapshot so module activation propagates live.
 *
 * Contract: packages/contracts/src/core-api.ts (TenantService).
 */
import type { TenantService, TenantSettings, Unsubscribe } from "@praesto/contracts";
import type { ModuleId, Tenant, TenantNotificationSettings } from "@praesto/shared-types";
import { onAuthStateChanged, onIdTokenChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import type { RuntimeContext } from "../internal/context";
import { tenantPath, tenantRoot } from "../internal/paths";

type SettingsListener = (s: TenantSettings) => void;

export function createTenantService(ctx: RuntimeContext): TenantService {
  const { db, auth } = ctx.firebase;
  let tenantCache: Tenant | null = null;
  let settingsCache: TenantSettings | null = null;
  const listeners = new Set<SettingsListener>();
  let unsubTenant: (() => void) | null = null;
  let unsubSettings: (() => void) | null = null;

  // MED-04: defer onSnapshot attach until Firebase Auth has resolved a
  // signed-in user AND the freshest ID token is ready. Attaching at
  // module-load fires a request with no bearer → firestore.rules
  // `isMember()` denies → noisy permission-denied in the console. Even
  // after auth fires, attaching too quickly races the SDK's internal
  // token-injection pipeline; forcing a getIdToken(true) refresh before
  // wiring the listeners flushes the queue cleanly.
  // A snapshot listener that errors is DONE: Firestore detaches it and never
  // retries. Without an error callback the failure was silent, `settingsCache`
  // stayed null, and the boot gate waited on it forever — the "Avvio in corso…"
  // that only a manual refresh cured. The usual cause is a token whose custom
  // claims have not propagated yet, which a forced refresh fixes, so retry once
  // and only once (a genuine permission problem must not become a loop).
  let retried = false;
  const retryOnce = (what: string, err: unknown, detach: () => void): void => {
    console.warn(`[tenant] ${what} listener failed`, err);
    detach();
    if (retried) return;
    retried = true;
    const u = auth.currentUser;
    if (!u) return;
    void u.getIdToken(true).then(() => attach());
  };

  // Set once the first post-sign-in attach has run, so the token listener
  // below never attaches on the cached token at rehydration (MED-04).
  let armed = false;

  onAuthStateChanged(auth, (u) => {
    if (!u) {
      unsubTenant?.();
      unsubSettings?.();
      unsubTenant = null;
      unsubSettings = null;
      tenantCache = null;
      settingsCache = null;
      armed = false;
      return;
    }
    void u.getIdToken(true).then(() => attach());
  });

  // The one retry above can be spent before the claims exist at all: right
  // after self-service signup the owner's claims are written by a trigger, and
  // both attempts may beat it. A later token — the owner signing in again, a
  // forced refresh, the hourly renewal — is the moment the claims can be there,
  // and for the same uid `onAuthStateChanged` does not fire. Re-attach whatever
  // is dead on every new token; live listeners are left alone (attach() skips
  // them), so this costs nothing in the normal case and cannot loop: a denied
  // re-attach only waits for the next token.
  onIdTokenChanged(auth, (u) => {
    if (u && armed) attach();
  });

  function attach(): void {
    armed = true;
    {
      if (!unsubTenant) {
        unsubTenant = onSnapshot(
          doc(db, tenantRoot(ctx.tenantId)),
          (snap) => {
            const d = snap.data();
            if (d) tenantCache = d as Tenant;
          },
          (err) =>
            retryOnce("tenant", err, () => {
              unsubTenant?.();
              unsubTenant = null;
            }),
        );
      }
      if (!unsubSettings) {
        unsubSettings = onSnapshot(
          doc(db, tenantPath(ctx.tenantId, "settings", "general")),
          (snap) => {
            const d = snap.data();
            if (!d) return;
            settingsCache = {
              activeModules: (d.activeModules ?? []) as ReadonlyArray<ModuleId>,
              defaultLocale: d.defaultLocale ?? "it",
              residency: "EU",
              sessionDuration: d.sessionDuration ?? {
                mobile: "P30D",
                desktop: "PT12H",
              },
              ...(d.notifications
                ? { notifications: d.notifications as TenantNotificationSettings }
                : {}),
              // Surfaced so the web onboarding guard (apps/web/src/onboarding/
              // OnboardingGuard.tsx) can read `settings().onboarding?.status`.
              // Absent on tenants provisioned before this field existed —
              // the guard treats "absent" like "completed" (never redirects).
              ...(d.onboarding
                ? { onboarding: d.onboarding as { status: "pending" | "completed" } }
                : {}),
            };
            for (const l of Array.from(listeners)) l(settingsCache);
          },
          (err) =>
            retryOnce("settings", err, () => {
              unsubSettings?.();
              unsubSettings = null;
            }),
        );
      }
    }
  }

  const requireSettings = (): TenantSettings => {
    if (!settingsCache) throw new Error("[tenant] settings not loaded yet");
    return settingsCache;
  };

  return {
    current(): Tenant {
      if (!tenantCache) throw new Error("[tenant] tenant not loaded yet");
      return tenantCache;
    },
    activeModules: () => settingsCache?.activeModules ?? [],
    isModuleActive: (id: ModuleId) => (settingsCache?.activeModules ?? []).includes(id),
    settings: requireSettings,
    onSettingsChange(cb: SettingsListener): Unsubscribe {
      listeners.add(cb);
      if (settingsCache) cb(settingsCache);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}
